import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  FileText, Plus, Edit2, Trash2, Eye, EyeOff, Loader, 
  Wand2, ExternalLink, Copy, Check, ChevronRight, ChevronLeft,
  Palette, Layout, Type, Image, Users, HelpCircle, Shield, Sparkles, Lock,
  Upload, X, Camera, Brush
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SalesPageBuilder = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [accessError, setAccessError] = useState('');
  const [templates, setTemplates] = useState({});
  const [colorSchemes, setColorSchemes] = useState({});
  const [pages, setPages] = useState([]);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  
  const [formData, setFormData] = useState({
    template: '',
    product_name: '',
    product_description: '',
    target_audience: '',
    main_benefit: '',
    price: '',
    original_price: '',
    color_scheme: 'purple',
    include_testimonials: true,
    include_faq: true,
    include_guarantee: true,
    guarantee_days: 7,
    cta_text: 'Comprar Agora',
    cta_link: ''
  });

  // Estado da etapa de identidade visual (Premium)
  const [productImages, setProductImages] = useState([]);
  const [brandImages, setBrandImages] = useState([]);
  const [logoFile, setLogoFile] = useState(null); // {file_id, url, filename}
  const [videoFiles, setVideoFiles] = useState([]); // [{file_id, url, filename}]
  const [uploadingProduct, setUploadingProduct] = useState(false);
  const [uploadingBrand, setUploadingBrand] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [visualProfile, setVisualProfile] = useState(null);
  const [imageAnalyses, setImageAnalyses] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pages/templates`, {
        withCredentials: true
      });
      setTemplates(response.data.templates);
      setColorSchemes(response.data.color_schemes);
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
        setAccessError(error.response?.data?.detail || 'Acesso negado');
      }
    }
  }, []);

  const fetchPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pages`, {
        withCredentials: true
      });
      setPages(response.data.pages);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Erro ao carregar páginas');
      }
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchTemplates();
      await fetchPages();
      setLoading(false);
    };
    loadData();
  }, [fetchTemplates, fetchPages]);

  const resetWizard = () => {
    setIsWizardOpen(false);
    setWizardStep(1);
    setFormData({
      template: '',
      product_name: '',
      product_description: '',
      target_audience: '',
      main_benefit: '',
      price: '',
      original_price: '',
      color_scheme: 'purple',
      include_testimonials: true,
      include_faq: true,
      include_guarantee: true,
      guarantee_days: 7,
      cta_text: 'Comprar Agora',
      cta_link: ''
    });
    setProductImages([]);
    setBrandImages([]);
    setLogoFile(null);
    setVideoFiles([]);
    setVisualProfile(null);
    setImageAnalyses(null);
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const resp = await axios.post(`${API_URL}/upload/product-image`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return { file_id: resp.data.file_id, url: resp.data.url, filename: file.name, is_video: resp.data.is_video };
  };

  const handleLogoUpload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const res = await uploadFile(f);
      setLogoFile(res);
      toast.success('Logo carregado');
    } catch (err) {
      toast.error('Erro ao enviar logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingVideo(true);
    try {
      const results = await Promise.all(files.map(uploadFile));
      setVideoFiles(prev => [...prev, ...results].slice(0, 3));
      toast.success(`${results.length} vídeo(s) carregado(s)`);
    } catch (err) {
      toast.error('Erro ao enviar vídeos');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const handleProductImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingProduct(true);
    try {
      const results = await Promise.all(files.map(uploadFile));
      setProductImages(prev => [...prev, ...results].slice(0, 6));
      toast.success(`${results.length} imagem(ns) do produto carregada(s)`);
    } catch (err) {
      toast.error('Erro ao enviar imagens do produto');
    } finally {
      setUploadingProduct(false);
      e.target.value = '';
    }
  };

  const handleBrandImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingBrand(true);
    try {
      const results = await Promise.all(files.map(uploadFile));
      setBrandImages(prev => [...prev, ...results].slice(0, 6));
      toast.success(`${results.length} arquivo(s) de identidade visual carregado(s)`);
    } catch (err) {
      toast.error('Erro ao enviar imagens da marca');
    } finally {
      setUploadingBrand(false);
      e.target.value = '';
    }
  };

  const handleAnalyzeBrand = async () => {
    if (!productImages.length && !brandImages.length) {
      toast.error('Envie pelo menos uma imagem para analisar');
      return;
    }
    setAnalyzing(true);
    try {
      const resp = await axios.post(`${API_URL}/pages/analyze-brand`, {
        product_image_ids: productImages.map(i => i.file_id),
        brand_image_ids: brandImages.map(i => i.file_id),
        product_name: formData.product_name,
        product_description: formData.product_description,
        target_audience: formData.target_audience,
      }, { withCredentials: true });

      if (resp.data.success && resp.data.profile) {
        setVisualProfile(resp.data.profile);
        setImageAnalyses(resp.data.image_analyses || []);
        toast.success('Perfil visual gerado! Veja abaixo as sugestões da IA.');
      } else {
        toast.error(resp.data.error || 'Falha na análise visual');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao analisar marca');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreatePage = async () => {
    setCreating(true);
    try {
      const premium = !!(productImages.length || brandImages.length || logoFile || videoFiles.length || visualProfile);
      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        product_image_ids: productImages.map(i => i.file_id),
        brand_image_ids: brandImages.map(i => i.file_id),
        logo_file_id: logoFile?.file_id || null,
        video_file_ids: videoFiles.map(v => v.file_id),
        visual_profile: visualProfile,
        image_analyses: imageAnalyses,
        premium_mode: premium,
      };

      const response = await axios.post(`${API_URL}/pages`, payload, {
        withCredentials: true
      });

      toast.success(response.data.message);
      if (response.data.premium_mode) {
        toast.info('Página PREMIUM gerada com análise visual da marca!');
      } else if (response.data.ai_success) {
        toast.info('Conteúdo gerado com IA!');
      }

      resetWizard();
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar página');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (pageId, currentStatus) => {
    try {
      const endpoint = currentStatus === 'published' ? 'unpublish' : 'publish';
      await axios.post(`${API_URL}/pages/${pageId}/${endpoint}`, {}, {
        withCredentials: true
      });
      toast.success(currentStatus === 'published' ? 'Página despublicada' : 'Página publicada!');
      fetchPages();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta página?')) return;
    
    try {
      await axios.delete(`${API_URL}/pages/${pageId}`, {
        withCredentials: true
      });
      toast.success('Página excluída!');
      fetchPages();
    } catch (error) {
      toast.error('Erro ao excluir página');
    }
  };

  const handleRegenerate = async (pageId) => {
    if (!window.confirm('Regenerar conteúdo com IA? (Consome 3 créditos)')) return;
    
    try {
      const response = await axios.post(`${API_URL}/pages/${pageId}/regenerate`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao regenerar');
    }
  };

  const copyToClipboard = (slug) => {
    const url = `${window.location.origin}/p/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    toast.success('Link copiado!');
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Eye className="w-3 h-3 mr-1" />
            Publicada
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <EyeOff className="w-3 h-3 mr-1" />
            Rascunho
          </span>
        );
      default:
        return null;
    }
  };

  const getTemplateIcon = (template) => {
    switch (template) {
      case 'produto_digital': return <FileText className="w-5 h-5" />;
      case 'servico': return <Users className="w-5 h-5" />;
      case 'produto_fisico': return <Image className="w-5 h-5" />;
      case 'lancamento': return <Sparkles className="w-5 h-5" />;
      default: return <Layout className="w-5 h-5" />;
    }
  };

  // Se não tem acesso (plano não Pro+)
  if (!hasAccess && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Criador de Páginas de Venda
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {accessError || 'Este recurso está disponível a partir do plano Pro.'}
            </p>
            <Button
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              onClick={() => window.location.href = '/seller/dashboard'}
            >
              Fazer Upgrade
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="page-builder-title">
              Páginas de Venda
            </h1>
            <p className="text-lg text-gray-600">
              Crie landing pages de alta conversão com IA
            </p>
          </div>
          <Button
            onClick={() => setIsWizardOpen(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="create-page-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Página
          </Button>
        </div>

        {/* Info */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <Wand2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-indigo-800">
              <p className="font-medium mb-1">Geração de Conteúdo com IA</p>
              <p>Suas páginas são criadas automaticamente com textos persuasivos gerados por IA. Você pode editar e personalizar depois.</p>
              <p className="mt-1 text-indigo-600 font-medium">Custo: 5 créditos por página | 3 créditos para regenerar</p>
            </div>
          </div>
        </div>

        {/* Pages List */}
        {pages.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhuma página ainda</h3>
            <p className="text-gray-600 mb-6">Crie sua primeira página de venda em minutos!</p>
            <Button onClick={() => setIsWizardOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Página
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pages.map((page) => (
              <div
                key={page.page_id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`page-${page.page_id}`}
              >
                {/* Color Preview */}
                <div 
                  className="h-24 flex items-center justify-center"
                  style={{ backgroundColor: page.colors?.accent || '#f3e8ff' }}
                >
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: page.colors?.primary || '#7c3aed' }}
                  >
                    {getTemplateIcon(page.template)}
                    <span className="text-white">{getTemplateIcon(page.template)}</span>
                  </div>
                </div>
                
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-1">{page.title}</h3>
                    {getStatusBadge(page.status)}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-1">
                    Template: {templates[page.template]?.name || page.template}
                  </p>
                  
                  <p className="text-xs text-gray-500 mb-4">
                    {page.views} visualizações
                  </p>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {page.status === 'published' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/p/${page.slug}`, '_blank')}
                        data-testid={`view-page-${page.page_id}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Ver
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(page.slug)}
                      data-testid={`copy-link-${page.page_id}`}
                    >
                      {copiedSlug === page.slug ? (
                        <Check className="w-3 h-3 mr-1" />
                      ) : (
                        <Copy className="w-3 h-3 mr-1" />
                      )}
                      Link
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePublish(page.page_id, page.status)}
                      data-testid={`toggle-publish-${page.page_id}`}
                    >
                      {page.status === 'published' ? (
                        <><EyeOff className="w-3 h-3 mr-1" /> Despublicar</>
                      ) : (
                        <><Eye className="w-3 h-3 mr-1" /> Publicar</>
                      )}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRegenerate(page.page_id)}
                      title="Regenerar conteúdo com IA"
                    >
                      <Wand2 className="w-3 h-3" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(page.page_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-page-${page.page_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Wizard Dialog */}
      <Dialog open={isWizardOpen} onOpenChange={setIsWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="wizard-title">
              <Wand2 className="w-5 h-5 text-indigo-600" />
              Criar Página de Venda
            </DialogTitle>
            <DialogDescription>
              Passo {wizardStep} de 4 — {wizardStep === 1 ? 'Escolha o Template' : wizardStep === 2 ? 'Informações do Produto' : wizardStep === 3 ? 'Identidade Visual (IA)' : 'Personalização'}
            </DialogDescription>
          </DialogHeader>

          {/* Progress Bar */}
          <div className="flex gap-2 mb-6">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  step <= wizardStep ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Step 1: Template Selection */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Escolha o template mais adequado para seu produto ou serviço:
              </p>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(templates).map(([key, template]) => (
                  <button
                    key={key}
                    onClick={() => setFormData(prev => ({ ...prev, template: key }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.template === key
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`template-${key}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.template === key ? 'bg-indigo-100' : 'bg-gray-100'
                      }`}>
                        {getTemplateIcon(key)}
                      </div>
                      <span className="font-medium">{template.name}</span>
                    </div>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Product Info */}
          {wizardStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Nome do Produto/Serviço *</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                  placeholder="Ex: Curso de Marketing Digital"
                  data-testid="product-name-input"
                />
              </div>
              
              <div>
                <Label>Descrição *</Label>
                <Textarea
                  value={formData.product_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, product_description: e.target.value }))}
                  placeholder="Descreva seu produto em detalhes..."
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>
              
              <div>
                <Label>Público-Alvo *</Label>
                <Input
                  value={formData.target_audience}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                  placeholder="Ex: Empreendedores iniciantes, profissionais de marketing"
                  data-testid="target-audience-input"
                />
              </div>
              
              <div>
                <Label>Benefício Principal *</Label>
                <Input
                  value={formData.main_benefit}
                  onChange={(e) => setFormData(prev => ({ ...prev, main_benefit: e.target.value }))}
                  placeholder="Ex: Aumente suas vendas em 3x em 30 dias"
                  data-testid="main-benefit-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preço com Desconto (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="297.00"
                    data-testid="price-input"
                  />
                </div>
                <div>
                  <Label>Preço Original (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value }))}
                    placeholder="497.00"
                    data-testid="original-price-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Identidade Visual (Premium - opcional) */}
          {wizardStep === 3 && (
            <div className="space-y-5">
              <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Modo Premium — Análise Visual por IA</p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Envie imagens do seu <strong>produto</strong> e da <strong>identidade visual da sua marca</strong> (logos, banners, prints de redes sociais, materiais publicitários).
                      A IA analisa cores, estilo, tom e posicionamento — gerando uma Landing Page única, alinhada à sua marca.
                      <span className="text-gray-500"> Esta etapa é opcional, mas recomendada para máxima personalização.</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Images */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Camera className="w-4 h-4 text-indigo-600" />
                  Imagens do Produto <span className="text-xs text-gray-500 font-normal">(até 6)</span>
                </Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                  <input
                    type="file"
                    id="product-images-input"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleProductImageUpload}
                    className="hidden"
                    disabled={uploadingProduct || productImages.length >= 6}
                    data-testid="product-images-input"
                  />
                  <label
                    htmlFor="product-images-input"
                    className={`flex flex-col items-center justify-center gap-2 py-3 cursor-pointer ${(uploadingProduct || productImages.length >= 6) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploadingProduct ? (
                      <Loader className="w-6 h-6 text-indigo-600 animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">
                      {productImages.length >= 6 ? 'Limite atingido' : 'Clique para enviar imagens do produto'}
                    </span>
                    <span className="text-xs text-gray-400">JPG, PNG ou WEBP</span>
                  </label>
                </div>
                {productImages.length > 0 && (
                  <div className="grid grid-cols-6 gap-2 mt-3" data-testid="product-images-grid">
                    {productImages.map((img, idx) => (
                      <div key={img.file_id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={img.filename} className="w-full h-full object-cover" />
                        <button
                          onClick={() => setProductImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-product-img-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Brand Identity Images */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Brush className="w-4 h-4 text-purple-600" />
                  Identidade Visual da Marca <span className="text-xs text-gray-500 font-normal">(até 6 — logos, banners, prints)</span>
                </Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                  <input
                    type="file"
                    id="brand-images-input"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleBrandImageUpload}
                    className="hidden"
                    disabled={uploadingBrand || brandImages.length >= 6}
                    data-testid="brand-images-input"
                  />
                  <label
                    htmlFor="brand-images-input"
                    className={`flex flex-col items-center justify-center gap-2 py-3 cursor-pointer ${(uploadingBrand || brandImages.length >= 6) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploadingBrand ? (
                      <Loader className="w-6 h-6 text-purple-600 animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">
                      {brandImages.length >= 6 ? 'Limite atingido' : 'Clique para enviar materiais da marca'}
                    </span>
                    <span className="text-xs text-gray-400">Logos, banners, materiais publicitários, prints de redes sociais</span>
                  </label>
                </div>
                {brandImages.length > 0 && (
                  <div className="grid grid-cols-6 gap-2 mt-3" data-testid="brand-images-grid">
                    {brandImages.map((img, idx) => (
                      <div key={img.file_id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={img.filename} className="w-full h-full object-cover" />
                        <button
                          onClick={() => setBrandImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-brand-img-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Logo (single) */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  Logotipo da Marca <span className="text-xs text-gray-500 font-normal">(recomendado — aparecerá no cabeçalho e rodapé)</span>
                </Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-amber-300 transition-colors">
                  <input
                    type="file"
                    id="logo-input"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploadingLogo}
                    data-testid="logo-input"
                  />
                  <label htmlFor="logo-input" className={`flex items-center justify-between cursor-pointer ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <div className="flex items-center gap-3">
                      {uploadingLogo ? (
                        <Loader className="w-6 h-6 text-amber-600 animate-spin" />
                      ) : logoFile ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${logoFile.url}`} alt="logo" className="w-12 h-12 object-contain bg-gray-50 rounded border border-gray-200" />
                      ) : (
                        <Upload className="w-6 h-6 text-gray-400" />
                      )}
                      <div>
                        <p className="text-sm text-gray-700 font-medium">
                          {logoFile ? logoFile.filename : 'Enviar logotipo (PNG com fundo transparente é ideal)'}
                        </p>
                        <p className="text-xs text-gray-400">Aparecerá no header/menu/footer da página</p>
                      </div>
                    </div>
                    {logoFile && (
                      <button
                        onClick={(ev) => { ev.preventDefault(); setLogoFile(null); }}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </label>
                </div>
              </div>

              {/* Videos */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-red-600" />
                  Vídeos do Produto ou Institucionais <span className="text-xs text-gray-500 font-normal">(até 3 — MP4, MOV, WEBM)</span>
                </Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-red-300 transition-colors">
                  <input
                    type="file"
                    id="video-input"
                    accept="video/mp4,video/quicktime,video/webm,video/*"
                    multiple
                    onChange={handleVideoUpload}
                    className="hidden"
                    disabled={uploadingVideo || videoFiles.length >= 3}
                    data-testid="video-input"
                  />
                  <label htmlFor="video-input" className={`flex flex-col items-center justify-center gap-2 py-3 cursor-pointer ${(uploadingVideo || videoFiles.length >= 3) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {uploadingVideo ? (
                      <Loader className="w-6 h-6 text-red-600 animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">
                      {videoFiles.length >= 3 ? 'Limite atingido' : 'Clique para enviar vídeos (máx 100MB cada)'}
                    </span>
                    <span className="text-xs text-gray-400">Serão usados no hero, seção institucional ou demonstração</span>
                  </label>
                </div>
                {videoFiles.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3" data-testid="video-files-grid">
                    {videoFiles.map((v, idx) => (
                      <div key={v.file_id} className="relative group aspect-video rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
                        <video src={`${process.env.REACT_APP_BACKEND_URL}${v.url}`} className="w-full h-full object-cover" muted playsInline />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-white opacity-70" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent">
                          <p className="text-[10px] text-white truncate">{v.filename}</p>
                        </div>
                        <button
                          onClick={() => setVideoFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-video-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Analyze Button + Preview */}
              {(productImages.length > 0 || brandImages.length > 0) && (
                <div>
                  <Button
                    onClick={handleAnalyzeBrand}
                    disabled={analyzing}
                    variant="outline"
                    className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="analyze-brand-btn"
                  >
                    {analyzing ? (
                      <><Loader className="w-4 h-4 mr-2 animate-spin" /> Analisando visualmente...</>
                    ) : visualProfile ? (
                      <><Sparkles className="w-4 h-4 mr-2" /> Reanalisar com IA (2 créditos)</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Analisar Marca com IA (2 créditos)</>
                    )}
                  </Button>
                </div>
              )}

              {/* Visual Profile Preview */}
              {visualProfile && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="visual-profile-preview">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <p className="text-sm font-semibold text-gray-900">Perfil Visual Identificado</p>
                  </div>

                  {visualProfile.colors && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Paleta da Marca</p>
                      <div className="flex gap-2">
                        {['primary', 'secondary', 'accent', 'neutral_dark', 'neutral_light'].map(k => (
                          visualProfile.colors[k] && (
                            <div key={k} className="flex flex-col items-center gap-1">
                              <div
                                className="w-10 h-10 rounded-lg border border-gray-200 shadow-sm"
                                style={{ backgroundColor: visualProfile.colors[k] }}
                                title={`${k}: ${visualProfile.colors[k]}`}
                              />
                              <span className="text-[10px] text-gray-500">{visualProfile.colors[k]}</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500 uppercase tracking-wide mb-1">Estilo</p>
                      <p className="text-gray-900 font-medium">{visualProfile.visual_style}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase tracking-wide mb-1">Voz</p>
                      <p className="text-gray-900 font-medium">{visualProfile.brand_voice}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase tracking-wide mb-1">Posicionamento</p>
                      <p className="text-gray-900 font-medium">{visualProfile.positioning}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase tracking-wide mb-1">Sofisticação</p>
                      <p className="text-gray-900 font-medium capitalize">{visualProfile.sophistication_level}</p>
                    </div>
                  </div>

                  {visualProfile.target_audience_visual && (
                    <div className="text-xs">
                      <p className="text-gray-500 uppercase tracking-wide mb-1">Público-alvo identificado</p>
                      <p className="text-gray-900">{visualProfile.target_audience_visual}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Customization */}
          {wizardStep === 4 && (
            <div className="space-y-6">
              <div>
                <Label className="mb-3 block">Esquema de Cores</Label>
                <div className="grid grid-cols-6 gap-2">
                  {Object.entries(colorSchemes).map(([key, colors]) => (
                    <button
                      key={key}
                      onClick={() => setFormData(prev => ({ ...prev, color_scheme: key }))}
                      className={`w-full aspect-square rounded-lg border-2 transition-all ${
                        formData.color_scheme === key
                          ? 'border-gray-900 scale-110'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: colors.primary }}
                      title={key}
                      data-testid={`color-${key}`}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Seções da Página</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_testimonials}
                      onChange={(e) => setFormData(prev => ({ ...prev, include_testimonials: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">Incluir Depoimentos</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_faq}
                      onChange={(e) => setFormData(prev => ({ ...prev, include_faq: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">Incluir FAQ</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_guarantee}
                      onChange={(e) => setFormData(prev => ({ ...prev, include_guarantee: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">Incluir Garantia</span>
                  </label>
                </div>
              </div>

              {formData.include_guarantee && (
                <div>
                  <Label>Dias de Garantia</Label>
                  <Input
                    type="number"
                    value={formData.guarantee_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, guarantee_days: parseInt(e.target.value) || 7 }))}
                    min="1"
                    max="365"
                    data-testid="guarantee-days-input"
                  />
                </div>
              )}

              <div>
                <Label>Texto do Botão CTA</Label>
                <Input
                  value={formData.cta_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                  placeholder="Comprar Agora"
                  data-testid="cta-text-input"
                />
              </div>

              <div>
                <Label>Link do Botão (opcional)</Label>
                <Input
                  type="url"
                  value={formData.cta_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                  placeholder="https://checkout.exemplo.com"
                  data-testid="cta-link-input"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => wizardStep > 1 ? setWizardStep(wizardStep - 1) : resetWizard()}
              data-testid="wizard-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {wizardStep > 1 ? 'Voltar' : 'Cancelar'}
            </Button>
            
            {wizardStep < 4 ? (
              <Button
                onClick={() => setWizardStep(wizardStep + 1)}
                disabled={
                  (wizardStep === 1 && !formData.template) ||
                  (wizardStep === 2 && (!formData.product_name || !formData.product_description || !formData.target_audience || !formData.main_benefit))
                }
                data-testid="wizard-next-btn"
              >
                {wizardStep === 3 ? 'Pular para Personalização' : 'Próximo'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleCreatePage}
                disabled={creating}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="wizard-create-btn"
              >
                {creating ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Gerando com IA...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    {(productImages.length || brandImages.length || visualProfile) ? 'Criar Página Premium (5 créditos)' : 'Criar Página (5 créditos)'}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
